import {
  Box,
  Card,
  Typography,
  Stack,
  Link,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import theme from '../../../../theme';

interface SharedPostCardProps {
  title: string;
  images: string[];
  caption?: string;
}

function SharedPostCard({
  title,
  images,
  caption = '',
}: SharedPostCardProps) {
  const [showFullCaption, setShowFullCaption] = useState(false);
  const maxCaptionLength = 200;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const photoGap = 6;
  const cols = images.length === 4 || isMobile ? 2 : 3;
  const shouldTruncate = caption.length > maxCaptionLength;
  const displayCaption = showFullCaption || !shouldTruncate
    ? caption
    : `${caption.substring(0, maxCaptionLength)}...`;

  return (
    <Card
      sx={{
        borderRadius: '12px',
        padding: 3,
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Stack spacing={3}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: 0,
          }}
        >
          {title}
        </Typography>

        {images.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: `${photoGap}px`,
              width: '100%',
              justifyContent: 'center',
            }}
          >
            {images.map((img, idx) => (
              <Box
                key={`${img}-${idx}`}
                sx={{
                  flexGrow: 1,
                  width: `calc((100% - ${photoGap * (cols - 1)}px) / ${cols})`,
                  aspectRatio: '1/1',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  maxWidth: '50vh',
                }}
              >
                <img
                  src={img}
                  alt={`${title} ${idx + 1}`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {caption && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                lineHeight: 1.4,
                color: 'text.primary',
              }}
            >
              {displayCaption}
            </Typography>
            {shouldTruncate && (
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowFullCaption(!showFullCaption)}
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.4,
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  p: 0,
                  fontFamily: 'inherit',
                }}
              >
                {showFullCaption ? 'Show less' : 'Read more'}
              </Link>
            )}
          </Box>
        )}
      </Stack>
    </Card>
  );
}

export default SharedPostCard;
